let CollectionLayout = {};


CollectionLayout = (function () {
  function positiveInteger(value, fallback = 1) {
    const numeric = Math.floor(Number(value));
    return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
  }

  function nonNegativeNumber(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
  }

  function fitAxis(available, item, gap) {
    const availableSize = nonNegativeNumber(available);
    const itemSize = nonNegativeNumber(item);
    if (availableSize <= 0 || itemSize <= 0) return 0;
    const spacing = nonNegativeNumber(gap);
    return Math.max(0, Math.floor((availableSize + spacing) / (itemSize + spacing)));
  }

  /**
   * Scale an item down so a row of them fits `available`.
   *
   * `minScale` is a legibility floor, and it matters: without one this shrank
   * a collection card to 15x38px on an 844x288 phone page box (scale 0.372) —
   * a smear too small to read or aim at, with its Stellar ribbon larger than
   * the card beneath it. A grid that cannot fit a readable row should show
   * FEWER cards and let pagination carry the rest, never shrink past the point
   * of being a card. The default keeps the old behaviour for callers that have
   * not chosen a floor.
   */
  function fitItemScale({
    available,
    item,
    preferredScale = 1,
    inset = 0,
    safetyInset = 0,
    minScale = 0.001,
  } = {}) {
    const itemSize = nonNegativeNumber(item);
    const preferred = nonNegativeNumber(preferredScale) || 1;
    const floor = Math.min(preferred, nonNegativeNumber(minScale) || 0.001);
    if (itemSize <= 0) return preferred;
    const usable = Math.max(
      0,
      nonNegativeNumber(available) - nonNegativeNumber(inset) - nonNegativeNumber(safetyInset),
    );
    if (usable <= 0) return preferred;
    const fitted = Math.min(preferred, usable / itemSize);
    return Math.max(floor, Math.floor(fitted * 1000) / 1000);
  }

  function fitReadableScale({
    itemWidth,
    zoom = 1,
    readableWidth = 136,
    minScale = 1.05,
    maxScale = 2,
  } = {}) {
    const width = nonNegativeNumber(itemWidth);
    const shellZoom = nonNegativeNumber(zoom) || 1;
    const minimum = nonNegativeNumber(minScale) || 1;
    const maximum = Math.max(minimum, nonNegativeNumber(maxScale) || minimum);
    if (width <= 0) return minimum;
    const target = (nonNegativeNumber(readableWidth) * shellZoom) / width;
    const fitted = Math.min(maximum, Math.max(minimum, target));
    return Math.round(fitted * 1000) / 1000;
  }

  function fitCapacity({
    width,
    height,
    itemWidth,
    itemHeight,
    columnGap = 0,
    rowGap = 0,
    maxRows = Number.POSITIVE_INFINITY,
    maxItems = 1,
  } = {}) {
    const columns = fitAxis(width, itemWidth, columnGap);
    const measuredRows = Number.isFinite(Number(height)) && Number(height) > 0
      && Number.isFinite(Number(itemHeight)) && Number(itemHeight) > 0
      ? fitAxis(height, itemHeight, rowGap)
      : 1;
    const rows = Number.isFinite(Number(maxRows))
      ? Math.min(measuredRows, positiveInteger(maxRows))
      : measuredRows;
    return Math.min(positiveInteger(maxItems), Math.max(0, columns * rows));
  }

  /* The Collection's Cards grid: a page of at most `maxItems` (eight), in one
     row or two balanced rows, else one row of what fits. */
  function chooseBalancedGrid({
    width,
    height,
    itemWidth,
    itemHeight,
    columnGap = 0,
    rowGap = 0,
    maxItems = 1,
  } = {}) {
    const limit = positiveInteger(maxItems);
    const availableWidth = nonNegativeNumber(width);
    const availableHeight = nonNegativeNumber(height);
    const cardWidth = nonNegativeNumber(itemWidth);
    const cardHeight = nonNegativeNumber(itemHeight);
    const horizontalGap = nonNegativeNumber(columnGap);
    const verticalGap = nonNegativeNumber(rowGap);
    const fits = (columns, rows) => columns > 0 && rows > 0
      && ((columns * cardWidth) + ((columns - 1) * horizontalGap) <= availableWidth)
      && ((rows * cardHeight) + ((rows - 1) * verticalGap) <= availableHeight);

    if (fits(limit, 1)) {
      return { columns: limit, rows: 1, pageSize: limit };
    }

    const balancedColumns = Math.ceil(limit / 2);
    const balancedRows = Math.ceil(limit / balancedColumns);
    if (balancedColumns * balancedRows === limit && fits(balancedColumns, balancedRows)) {
      return { columns: balancedColumns, rows: balancedRows, pageSize: limit };
    }

    const columns = Math.min(limit, fitAxis(availableWidth, cardWidth, horizontalGap));
    if (columns > 0 && fits(columns, 1)) {
      return { columns, rows: 1, pageSize: columns };
    }
    return { columns: 0, rows: 0, pageSize: 0 };
  }

  /**
   * A desktop catalog page that fills its pane. `itemWidth`/`itemHeight` are
   * the preferred (readable) card; cards may give up to `minScale` of it so
   * one more row fits, and never grow past it. Of every row count that keeps
   * cards at or above that floor, the one showing the most cards wins, the
   * larger cards on a tie. `scale` multiplies the preferred card; a page of 0
   * means not one row fits at the floor and the caller keeps its fallback.
   *
   * It replaced a page of at most eight that fell back to ONE row whenever a
   * second did not fit at full size: the owner's 1536x730 window showed six
   * cards between two empty bands (27 October 2026, "only 6 cards per page on
   * left side so that is weird"); it shows two rows of seven now.
   *
   * @param {{ width?: number, height?: number, itemWidth?: number, itemHeight?: number,
   *   columnGap?: number, rowGap?: number, minScale?: number, maxItems?: number }} [options]
   */
  function chooseFilledGrid({
    width,
    height,
    itemWidth,
    itemHeight,
    columnGap = 0,
    rowGap = 0,
    minScale = 1,
    maxItems = 1,
  } = {}) {
    const limit = positiveInteger(maxItems);
    const availableWidth = nonNegativeNumber(width);
    const availableHeight = nonNegativeNumber(height);
    const cardWidth = nonNegativeNumber(itemWidth);
    const cardHeight = nonNegativeNumber(itemHeight);
    const horizontalGap = nonNegativeNumber(columnGap);
    const verticalGap = nonNegativeNumber(rowGap);
    const floor = Math.min(1, nonNegativeNumber(minScale) || 1);
    let best = { columns: 0, rows: 0, pageSize: 0, scale: 0 };
    if (cardWidth <= 0 || cardHeight <= 0) return best;

    const maxRows = Math.min(limit, fitAxis(availableHeight, cardHeight * floor, verticalGap));
    for (let rows = 1; rows <= maxRows; rows += 1) {
      const rowHeight = (availableHeight - ((rows - 1) * verticalGap)) / rows;
      // Floored like fitItemScale, so rounding never adds a pixel to a row.
      const scale = Math.floor(Math.min(1, rowHeight / cardHeight) * 1000) / 1000;
      const columns = Math.min(
        Math.floor(limit / rows),
        fitAxis(availableWidth, cardWidth * scale, horizontalGap),
      );
      const pageSize = columns * rows;
      if (pageSize > best.pageSize || (pageSize === best.pageSize && pageSize > 0 && scale > best.scale)) {
        best = { columns, rows, pageSize, scale };
      }
    }
    return best;
  }

  function pageForLogicalOffset({ page, pageSize, nextPageSize } = {}) {
    const currentPage = positiveInteger(page);
    const currentPageSize = positiveInteger(pageSize);
    const normalizedNextPageSize = positiveInteger(nextPageSize);
    const logicalOffset = (currentPage - 1) * currentPageSize;
    return Math.floor(logicalOffset / normalizedNextPageSize) + 1;
  }

  function fitPaginatedCapacity({
    totalItems,
    unpaginatedHeight,
    paginatedHeight,
    itemHeight,
    gap = 0,
    maxItems = 1,
  } = {}) {
    const limit = positiveInteger(maxItems);
    const total = Math.max(0, Math.floor(Number(totalItems) || 0));
    const unpaginatedCapacity = Math.min(
      limit,
      fitAxis(unpaginatedHeight, itemHeight, gap),
    );
    const paginatedCapacity = Math.min(
      limit,
      fitAxis(paginatedHeight, itemHeight, gap),
    );
    const paginated = total > unpaginatedCapacity;
    const measuredPageSize = paginated ? paginatedCapacity : unpaginatedCapacity;

    return {
      pageSize: Math.max(1, measuredPageSize),
      paginated,
      unpaginatedCapacity,
      paginatedCapacity,
    };
  }

  return {
    chooseBalancedGrid,
    chooseFilledGrid,
    fitCapacity,
    fitItemScale,
    fitPaginatedCapacity,
    fitReadableScale,
    pageForLogicalOffset,
  };
})();

export { CollectionLayout };
